import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Modal from '../ui/Modal';
import TimeSlotSelector from './TimeSlotSelector';
import supabase from '../../utils/supabase';

/**
 * Enhanced Interview Schedule Modal
 * Integrates with time slot management system for optimized scheduling
 */
const EnhancedInterviewScheduleModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = null,
  selectedCandidate = null,
  currentUserId,
  preselectedDate = null,
  onOpenTimeSlotManager = null // Callback to open time slot manager
}) => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    date: '',
    selectedTimeSlot: null,
    customTime: {
      start: '',
      end: ''
    },
    useCustomTime: false,
    duration: 15,
    interview_type: 'video',
    meeting_link: '',
    location: '',
    notes: '',
    candidate_name: '',
    candidate_email: '',
    job_title: '',
    selectedApplication: null
  });

  // Available applications for selection
  const [applications, setApplications] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  
  // Applicant selection state
  const [applicantView, setApplicantView] = useState('compact'); // 'compact' or 'detailed'
  const [applicantFilters, setApplicantFilters] = useState({
    status: 'all', // 'all', 'pending', 'shortlisted'
    jobId: 'all',
    search: ''
  });
  const [jobs, setJobs] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  // Initialize form data when modal opens
  useEffect(() => {
    if (isOpen) {
      const today = new Date();
      const defaultDate = preselectedDate || 
        (initialData?.scheduled_at ? new Date(initialData.scheduled_at) : today);
        
      // Ensure the date is not in the past
      const minDate = new Date();
      minDate.setHours(0, 0, 0, 0);
      const selectedDate = defaultDate < minDate ? minDate : defaultDate;

      setFormData({
        date: selectedDate.toISOString().split('T')[0],
        selectedTimeSlot: null,
        customTime: {
          start: initialData?.scheduled_at ? 
            new Date(initialData.scheduled_at).toTimeString().slice(0, 5) : '10:00',
          end: initialData?.scheduled_at ? 
            new Date(new Date(initialData.scheduled_at).getTime() + (initialData.duration || 15) * 60000).toTimeString().slice(0, 5) : '10:15'
        },
        useCustomTime: !initialData?.time_slot_id,
        duration: initialData?.duration || 15,
        interview_type: 'video',
        meeting_link: initialData?.meeting_link || '',
        location: '',
        notes: initialData?.notes || '',
        candidate_name: selectedCandidate?.name || initialData?.candidate_name || '',
        candidate_email: selectedCandidate?.email || initialData?.candidate_email || '',
        job_title: selectedCandidate?.job_title || initialData?.job_title || '',
        selectedApplication: null
      });

      setSelectedTimeSlot(null);
      setError(null);
    }
  }, [isOpen, initialData, selectedCandidate, preselectedDate]);

  // Load employer's jobs for filtering
  const loadEmployerJobs = useCallback(async () => {
    if (!user?.id || userProfile?.role !== 'employer') return;

    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, status')
        .eq('employer_id', user.id)
        .in('status', ['active', 'paused'])
        .order('created_at', { ascending: false });

      if (error) throw error;
      setJobs(data || []);
    } catch (err) {
      // Error logged via ErrorHandler
    }
  }, [user?.id, userProfile?.role]);

  // Load applications for employer with enhanced filtering and data
  const loadApplications = useCallback(async () => {
    if (!user?.id || userProfile?.role !== 'employer') return;

    setLoadingApplications(true);
    try {
      let query = supabase
        .from('job_applications')
        .select(`
          *,
          jobs!inner (
            id,
            title,
            employer_id,
            skills_required,
            experience_level,
            job_type,
            hourly_rate_min,
            hourly_rate_max
          ),
          user_profiles!job_applications_applicant_id_fkey (
            id,
            full_name,
            email,
            phone,
            location,
            bio,
            skills,
            experience_years,
            hourly_rate,
            profile_picture,
            linkedin
          )
        `)
        .eq('jobs.employer_id', user.id);

      // Apply status filter
      if (applicantFilters.status !== 'all') {
        query = query.eq('status', applicantFilters.status);
      } else {
        query = query.in('status', ['pending', 'shortlisted', 'interviewed', 'accepted']);
      }

      // Apply job filter
      if (applicantFilters.jobId !== 'all') {
        query = query.eq('job_id', applicantFilters.jobId);
      }

      const { data, error } = await query
        .order('applied_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      // Apply search filter on frontend for name/email
      let filteredData = data || [];
      if (applicantFilters.search.trim()) {
        const searchTerm = applicantFilters.search.toLowerCase();
        filteredData = filteredData.filter(app => 
          app.user_profiles.full_name.toLowerCase().includes(searchTerm) ||
          app.user_profiles.email.toLowerCase().includes(searchTerm) ||
          app.jobs.title.toLowerCase().includes(searchTerm)
        );
      }

      setApplications(filteredData);
    } catch (err) {
      // Error logged via ErrorHandler
      setError('Failed to load applications');
    } finally {
      setLoadingApplications(false);
    }
  }, [user?.id, userProfile?.role, applicantFilters]);

  useEffect(() => {
    if (isOpen) {
      loadEmployerJobs();
      loadApplications();
    }
  }, [isOpen, loadEmployerJobs, loadApplications]);

  // Auto-select candidate from loaded applications when initialData or selectedCandidate is provided
  useEffect(() => {
    if (applications.length > 0 && (initialData || selectedCandidate)) {
      // First try to match by applicationId (most reliable)
      let matchingApplication = initialData?.applicationId
        ? applications.find(app => app.id === initialData.applicationId)
        : null;

      // Fallback: match by candidate email and job title
      if (!matchingApplication && initialData?.candidate_email) {
        matchingApplication = applications.find(app =>
          app.user_profiles.email === initialData.candidate_email &&
          app.jobs.title === initialData.job_title
        );
      }

      // Fallback: match by candidateId (from messaging context) and jobId
      if (!matchingApplication && (initialData?.candidateId || selectedCandidate?.id)) {
        const candidateId = initialData?.candidateId || selectedCandidate?.id;
        const jobId = initialData?.jobId;
        matchingApplication = applications.find(app =>
          app.user_profiles.id === candidateId &&
          (!jobId || app.jobs.id === jobId)
        );
        // If no job-specific match, try matching just by candidateId
        if (!matchingApplication) {
          matchingApplication = applications.find(app =>
            app.user_profiles.id === candidateId
          );
        }
      }

      if (matchingApplication) {
        handleApplicationSelect(matchingApplication);
      }
    }
  }, [
    applications,
    initialData?.applicationId,
    initialData?.candidate_email,
    initialData?.job_title,
    initialData?.candidateId,
    initialData?.jobId,
    selectedCandidate?.id
  ]);

  // Handle form field changes
  const handleChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  // Handle application selection
  const handleApplicationSelect = useCallback((application) => {
    setFormData(prev => ({
      ...prev,
      selectedApplication: application,
      candidate_name: application.user_profiles.full_name,
      candidate_email: application.user_profiles.email,
      job_title: application.jobs.title
    }));
  }, []);

  // Handle filter changes
  const handleFilterChange = useCallback((filterType, value) => {
    setApplicantFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  }, []);

  // Calculate match percentage for skills
  const calculateSkillMatch = useCallback((applicantSkills, requiredSkills) => {
    if (!applicantSkills || !requiredSkills || requiredSkills.length === 0) return 0;
    
    // Extract skill names from objects if necessary
    const extractSkillNames = (skills) => {
      if (!Array.isArray(skills)) return [];
      return skills.map(skill => {
        if (typeof skill === 'string') return skill;
        if (skill && typeof skill === 'object' && skill.name) return skill.name;
        return '';
      }).filter(Boolean);
    };
    
    const applicantSkillNames = extractSkillNames(applicantSkills);
    const requiredSkillNames = extractSkillNames(requiredSkills);
    
    const matches = requiredSkillNames.filter(skill => 
      applicantSkillNames.some(appSkill => 
        appSkill.toLowerCase().includes(skill.toLowerCase()) ||
        skill.toLowerCase().includes(appSkill.toLowerCase())
      )
    );
    
    return Math.round((matches.length / requiredSkillNames.length) * 100);
  }, []);

  // Get status badge style
  const getStatusBadgeStyle = useCallback((status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'shortlisted':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'interviewed':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  }, []);

  // Handle time slot selection
  const handleTimeSlotSelect = useCallback((slot) => {
    setSelectedTimeSlot(slot);
    setFormData(prev => ({
      ...prev,
      selectedTimeSlot: slot,
      useCustomTime: false,
      customTime: {
        start: slot.start.toTimeString().slice(0, 5),
        end: slot.end.toTimeString().slice(0, 5)
      }
    }));
  }, []);

  // Memoize the selected date to prevent unnecessary re-renders
  const memoizedSelectedDate = useMemo(() => {
    return formData.date ? new Date(formData.date) : null;
  }, [formData.date]);

  // Memoize the time slot manager callback (no-op fallback when onOpenTimeSlotManager prop is not provided)
  const memoizedTimeSlotManagerCallback = useCallback(() => {
    // Intentional no-op: time slot manager is controlled by parent via onOpenTimeSlotManager prop
  }, []);

  // Calculate end time based on start time and duration
  const calculateEndTime = useCallback((startTime, duration) => {
    if (!startTime) return '';
    
    const [hours, minutes] = startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const endMinutes = startMinutes + duration;
    
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    
    return `${endHours.toString().padStart(2, '0')}:${endMins.toString().padStart(2, '0')}`;
  }, []);

  // Update end time when start time or duration changes for custom time
  useEffect(() => {
    if (formData.useCustomTime && formData.customTime.start) {
      const newEndTime = calculateEndTime(formData.customTime.start, formData.duration);
      if (newEndTime !== formData.customTime.end) {
        setFormData(prev => ({
          ...prev,
          customTime: {
            ...prev.customTime,
            end: newEndTime
          }
        }));
      }
    }
  }, [formData.customTime.start, formData.duration, formData.useCustomTime, calculateEndTime]);

  // Form validation
  const validateForm = useCallback(() => {
    if (!formData.date) return 'Please select a date';
    if (!formData.candidate_name.trim()) return 'Please enter candidate name';
    if (!formData.candidate_email.trim()) return 'Please enter candidate email';
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.candidate_email)) {
      return 'Please enter a valid email address';
    }

    if (formData.useCustomTime) {
      if (!formData.customTime.start || !formData.customTime.end) {
        return 'Please set interview start and end times';
      }
      if (formData.customTime.start >= formData.customTime.end) {
        return 'End time must be after start time';
      }
    } else if (!selectedTimeSlot) {
      return 'Please select a time slot or use custom time';
    }

    if (!formData.meeting_link.trim()) {
      return 'Please provide a meeting link';
    }

    return null;
  }, [formData, selectedTimeSlot]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    try {
      // Determine the final scheduled time
      let scheduledAt;
      if (selectedTimeSlot) {
        scheduledAt = selectedTimeSlot.start.toISOString();
      } else {
        scheduledAt = new Date(`${formData.date}T${formData.customTime.start}:00`).toISOString();
      }

      // Prepare interview data
      const interviewData = {
        scheduled_at: scheduledAt,
        duration: formData.duration,
        interview_type: formData.interview_type,
        meeting_link: formData.meeting_link,
        location: formData.location,
        notes: formData.notes,
        candidate_name: formData.candidate_name,
        candidate_email: formData.candidate_email,
        job_title: formData.job_title,
        selectedApplication: formData.selectedApplication,
        time_slot_id: selectedTimeSlot?.timeSlotId || null,
        
        // Additional metadata
        interviewer_id: currentUserId || user.id,
        status: initialData ? initialData.status : 'scheduled'
      };

      await onSubmit(interviewData);
      
      // Close modal on success
      onClose();
    } catch (err) {
      // Error logged via ErrorHandler
      setError('Failed to save interview. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [formData, selectedTimeSlot, validateForm, onSubmit, onClose, currentUserId, user?.id, initialData]);

  const formatDate = useCallback((dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  const minDate = useMemo(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }, []);

  // Filtered applications for display
  const filteredApplications = useMemo(() => {
    return applications;
  }, [applications]);

  if (!isOpen) return null;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <div className="p-6 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-text-primary">
              {initialData ? 'Reschedule Interview' : 'Schedule Interview'}
            </h2>
            <button 
              onClick={onClose} 
              className="text-text-secondary hover:text-text-primary transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-red-700">
                <Icon name="AlertCircle" size={16} />
                <span className="text-sm">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Candidate Selection Dropdown */}
            {userProfile?.role === 'employer' && (
              <div>
                <label className="block text-sm font-medium mb-2 text-text-primary">
                  Select Candidate *
                </label>
                <select
                  value={formData.selectedApplication?.id || ''}
                  onChange={(e) => {
                    const selected = applications.find(app => app.id === e.target.value);
                    if (selected) {
                      handleApplicationSelect(selected);
                    }
                  }}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  required
                >
                  <option value="">Choose a candidate...</option>
                  {applications.map((app) => (
                    <option key={app.id} value={app.id}>
                      {app.user_profiles.full_name} • {app.jobs.title}
                    </option>
                  ))}
                </select>
                {formData.selectedApplication && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                    <div className="font-medium text-blue-900">{formData.candidate_name}</div>
                    <div className="text-blue-800">{formData.candidate_email}</div>
                    <div className="text-blue-700 text-xs mt-1">Position: {formData.job_title}</div>
                  </div>
                )}
              </div>
            )}

            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary">
                Interview Date *
              </label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => handleChange('date', e.target.value)}
                min={minDate}
                required
              />
              {formData.date && (
                <p className="text-sm text-text-secondary mt-1">
                  {formatDate(formData.date)}
                </p>
              )}
            </div>

            {/* Time Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-text-primary">
                  Interview Time *
                </label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/interview-management?modal=timeSlotManager');
                    }}
                    className="text-xs"
                  >
                    <Icon name="Settings" size={14} className="mr-1" />
                    Manage Slots
                  </Button>
                </div>
              </div>

              {/* Duration Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-text-primary">
                  Interview Duration
                </label>
                <select
                  value={formData.duration}
                  onChange={(e) => handleChange('duration', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>60 minutes</option>
                  <option value={90}>90 minutes</option>
                  <option value={120}>120 minutes</option>
                </select>
              </div>

              {/* Time Slot Selection */}
              {formData.date && (
                <div className="mb-4">
                  <TimeSlotSelector
                    selectedDate={memoizedSelectedDate}
                    employerId={currentUserId || user.id}
                    duration={formData.duration}
                    selectedSlot={selectedTimeSlot}
                    excludeInterviewId={initialData?.id}
                    onSlotSelect={handleTimeSlotSelect}
                    onOpenTimeSlotManager={onOpenTimeSlotManager}
                  />
                </div>
              )}

              {/* Custom Time Toggle and Inputs */}
              <div className="space-y-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="useCustomTime"
                    checked={formData.useCustomTime}
                    onChange={(e) => {
                      handleChange('useCustomTime', e.target.checked);
                      if (!e.target.checked) {
                        setSelectedTimeSlot(null);
                      }
                    }}
                    className="mr-2 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <label htmlFor="useCustomTime" className="text-sm text-text-secondary">
                    Use custom time (outside available slots)
                  </label>
                </div>

                {formData.useCustomTime && (
                  <div className="grid grid-cols-3 gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div>
                      <label className="block text-xs font-medium mb-1 text-text-primary">
                        Start Time
                      </label>
                      <Input
                        type="time"
                        value={formData.customTime.start}
                        onChange={(e) => handleChange('customTime', { 
                          ...formData.customTime, 
                          start: e.target.value 
                        })}
                        size="sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-text-primary">
                        Duration
                      </label>
                      <div className="text-sm p-2 bg-white border border-border rounded text-center">
                        {formData.duration} min
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1 text-text-primary">
                        End Time
                      </label>
                      <Input
                        type="time"
                        value={formData.customTime.end}
                        readOnly
                        size="sm"
                        className="bg-gray-50"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Interview Type - Video Call Only */}
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary">
                Interview Type
              </label>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                <Icon name="Video" size={20} className="text-primary" />
                <div>
                  <div className="font-medium text-blue-900">Video Call</div>
                  <div className="text-xs text-blue-700">All interviews are conducted via video conference</div>
                </div>
              </div>
            </div>

            {/* Meeting Link */}
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary">
                Meeting Link *
              </label>
              <Input
                type="url"
                value={formData.meeting_link}
                onChange={(e) => handleChange('meeting_link', e.target.value)}
                placeholder="https://zoom.us/j/123456789 or https://meet.google.com/abc-def-ghi"
              />
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-2 text-text-primary">
                Notes & Instructions
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                rows={3}
                placeholder="Any additional notes, instructions, or preparation materials for the interview..."
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
                className="flex-1"
              >
                {loading && <Icon name="Loader" size={16} className="animate-spin mr-2" />}
                {initialData ? 'Update Interview' : 'Schedule Interview'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </Modal>

    </>
  );
};

export default EnhancedInterviewScheduleModal;